import clsx from 'clsx';
import React, { JSX } from 'react';

type TypographyProps = {
  as?: keyof JSX.IntrinsicElements;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  size?: 'lg' | 'md' | 'sm' | 'xs';
  weight?: 'bd' | 'md' | 'lt' | 'bk';
  children: React.ReactNode;
  className?: string;
};

const Typography: React.FC<TypographyProps> = ({
  as: Tag = 'p', // Default to 'p' for paragraphs, but can be overridden to 'h1', 'h2', etc.
  variant, // Used for headings (e.g., h1, h2, h3, etc.)
  size = 'md',
  weight = 'md', // Font weight: bd, md, lt, bk
  className,
  children
}) => {
  // Define font sizes for headings and paragraphs
  const fontSize = {
    h1: 'text-5xl', // 48px
    h2: 'text-[40px]', // 40px
    h3: 'text-4xl', // 36px
    h4: 'text-[32px]', // 32px
    h5: 'text-2xl', // 24px
    h6: 'text-sm', // 14px
    lg: 'text-xl', // 20px
    md: 'text-base', // 16px
    sm: 'text-sm', // 14px
    xs: 'text-xs' // 12px
  };

  // Define font weights
  const fontWeight = {
    bd: 'font-bold', // Updated to more standard class names
    md: 'font-medium',
    lt: 'font-light',
    bk: 'font-book'
  };

  // Ensure that the variant (for headings) overrides the size for paragraphs
  const textSize = variant ? fontSize[variant] : fontSize[size];

  // Combine the classes using clsx, giving custom className higher priority by passing it last
  const classes = clsx(
    'font-futura',
    fontWeight[weight] || 'font-medium',
    textSize || 'text-base',
    className
  );

  return <Tag className={classes}>{children}</Tag>;
};

export default Typography;
